import api from '../api/client';

const SETTINGS_LS_KEYS = [
  'borsai.indicatorPrefs.v1',
  'borsai.indicatorStylePrefs.v1',
  'portfolioData',
];

const readJson = (raw) => {
  try {
    if (raw == null) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export function snapshotLocalStorageSettings() {
  const out = {};
  for (const key of SETTINGS_LS_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      const parsed = readJson(raw);
      if (parsed != null) out[key] = parsed;
    } catch {
      // ignore
    }
  }
  return out;
}

export async function fetchUserSettings() {
  const res = await api.get('/user/settings');
  return res?.data ?? res;
}

export async function persistUserSettings(partialUpdate) {
  // API expects full UserSettings shape; backend ignores user_id/created_at/last_updated.
  // We fetch current server settings, merge, and PUT.
  const current = await fetchUserSettings();
  const next = {
    ...(current || {}),
    ...(partialUpdate || {}),
  };
  const res = await api.put('/user/settings', next);
  return res?.data ?? res;
}

export async function ensureSettingsMigratedFromLocalStorage() {
  const server = await fetchUserSettings();
  const clientSettings = server?.client_settings || {};

  // If DB is missing the client_settings bucket (or empty), migrate.
  const needsMigration = !clientSettings || Object.keys(clientSettings).length === 0;
  if (!needsMigration) return server;

  const snapshot = snapshotLocalStorageSettings();
  if (!Object.keys(snapshot).length) return server;

  return await persistUserSettings({
    client_settings: {
      ...(clientSettings || {}),
      localStorage: snapshot,
    },
  });
}

export function rehydrateLocalStorageFromServerSettings(serverSettings) {
  const ls = serverSettings?.client_settings?.localStorage;
  if (!ls || typeof ls !== 'object') return;
  for (const key of SETTINGS_LS_KEYS) {
    try {
      if (typeof ls[key] === 'undefined') continue;
      localStorage.setItem(key, JSON.stringify(ls[key]));
    } catch {
      // ignore
    }
  }
}

let debounceId = null;
let pending = null;

export function schedulePersistClientSettings(update, debounceMs = 250) {
  pending = { ...(pending || {}), ...(update || {}) };
  if (debounceId) window.clearTimeout(debounceId);
  debounceId = window.setTimeout(async () => {
    const toSend = pending;
    pending = null;
    debounceId = null;
    try {
      await persistUserSettings({ client_settings: toSend });
    } catch (e) {
      // keep silent; app should remain usable offline
      // console.warn('settings sync failed', e);
    }
  }, debounceMs);
}
